import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getMyClubs } from '../../services/clubService';
import { Club } from '../../types/clubs';
import { UserGroupIcon, MapPinIcon, CheckCircleIcon, ClockIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';

const MyClubsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMyClubs();
  }, []);

  const loadMyClubs = async () => {
    try {
      setError(null);
      const fetchedClubs = await getMyClubs();
      setClubs(fetchedClubs);
    } catch (err: any) {
      console.error('Error fetching my clubs:', err);
      setError(err.message || t('common.error') || 'Failed to load your clubs');
    } finally {
      setLoading(false);
    }
  };

  const getMembershipStatusInfo = (club: Club) => {
    if (!club.membership) return null;
    
    const status = club.membership.status;
    if (status === 'ACTIVE') {
      return { label: t('clubs.activeMember') || 'Active Member', color: 'text-green-600 bg-green-50', icon: CheckCircleIcon };
    } else if (status === 'PENDING') {
      return { label: t('clubs.pendingApproval') || 'Pending Approval', color: 'text-orange-600 bg-orange-50', icon: ClockIcon };
    } else if (status === 'REJECTED') {
      return { label: t('clubs.rejected') || 'Rejected', color: 'text-red-600 bg-red-50', icon: XMarkIcon };
    } else if (status === 'EXPIRED') {
      return { label: t('clubs.expired') || 'Expired', color: 'text-gray-600 bg-gray-50', icon: ClockIcon };
    } else if (status === 'SUSPENDED') {
      return { label: t('clubs.suspended') || 'Suspended', color: 'text-red-600 bg-red-50', icon: XMarkIcon };
    } else if (status === 'CANCELLED') {
      return { label: t('clubs.cancelled') || 'Cancelled', color: 'text-gray-600 bg-gray-50', icon: XMarkIcon };
    }
    return null;
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center">{t('common.loading') || 'Loading your clubs...'}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadMyClubs}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            {t('common.retry') || 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('clubs.myClubs') || 'My Clubs'}</h1>

      {clubs.length === 0 ? (
        <div className="text-center py-12">
          <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t('clubs.noMemberships') || 'You are not a member of any clubs yet'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t('clubs.discoverClubs') || 'Discover clubs to join'}
          </p>
          <Link
            to="/discover/clubs"
            className="mt-4 inline-block px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            {t('clubs.discoverClubsButton') || 'Discover Clubs'}
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clubs.map((club) => {
            const statusInfo = getMembershipStatusInfo(club);
            return (
              <Link
                key={club.id}
                to={`/clubs/${club.id}`}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow p-6"
              >
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{club.name}</h3>
                  {statusInfo && (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                      <statusInfo.icon className="h-4 w-4 mr-1" />
                      <span>{statusInfo.label}</span>
                    </span>
                  )}
                </div>
                {club.location && (
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <MapPinIcon className="h-4 w-4 mr-1" />
                    <span>{club.location}</span>
                  </div>
                )}
                {club.membership?.joinedAt && (
                  <div className="text-sm text-gray-600 mb-2">
                    {t('clubs.joinedOn') || 'Joined'} {format(new Date(club.membership.joinedAt), 'MMM dd, yyyy')}
                  </div>
                )}
                {club.membership?.nextPaymentDueAt && club.membership.paymentStatus === 'ACTIVE' && (
                  <div className="text-sm text-primary-600 font-medium">
                    {t('clubs.nextPayment') || 'Next payment'} {format(new Date(club.membership.nextPaymentDueAt), 'MMM dd, yyyy')}
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyClubsPage;
